/**
 * The client's reports (2026-11-03, approved R1 with R2's trend tiles and R3's reader): every report on one timeline by
 * month, each with the headline numbers it states and the work done between reports; the numbers that recur shown as
 * trends on top; "Read" opens the report here, stepping back and forward through time, with "Ask about it" beside it.
 */
import { useMemo, useState } from 'react';
import { Empty } from '@caios/ui';
import './reports-timeline.css';

export type Headline = { label: string; value: string; delta?: { dir?: string; text?: string } };
export type TimelineReport = { id: string; type: string; title: string; period_label: string; period_start?: string; period_end: string; published_at: string | null; headline?: Headline[] };
/** "5 Sept – 1 Nov 2026": the portal's one date style, made from the stored dates (the stored label is US-style). */
export const periodOf = (r: Pick<TimelineReport, 'period_label' | 'period_start' | 'period_end'>) => {
  if (!r.period_start || !r.period_end) return r.period_label;
  const f = (d: string, y: boolean) => new Date(`${d.slice(0, 10)}T12:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', ...(y ? { year: 'numeric' } : {}), timeZone: 'UTC' });
  return `${f(r.period_start, r.period_start.slice(0, 4) !== r.period_end.slice(0, 4))} – ${f(r.period_end, true)}`;
};
type WorkItem = { id: string; title: string; on_day: string };

const KIND = (type: string) => (/seo/.test(type) ? 'SEO' : /paid|ads|media/.test(type) ? 'Paid media' : /social/.test(type) ? 'Social' : 'Report');
const WEEKLY = (type: string) => /weekly/.test(type);
const monthOf = (d: string) => new Date(`${d.slice(0, 10)}T12:00:00`).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
const day = (d: string | null) => (d ? new Date(`${d.slice(0, 10)}T12:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '');
const num = (v: string) => { const n = Number(String(v).replace(/[^0-9.\-]/g, '')); return Number.isFinite(n) && /\d/.test(v) ? n : null; };

/** A number the reports repeat (same label, same kind of report) becomes a trend tile: latest value, its change, a line. */
function trends(reports: TimelineReport[]) {
  const series = new Map<string, { label: string; points: { at: string; v: number; raw: Headline }[] }>();
  for (const r of [...reports].sort((a, b) => a.period_end.localeCompare(b.period_end))) {
    for (const h of r.headline ?? []) {
      const v = num(h.value); if (v == null) continue;
      const key = `${KIND(r.type)}|${h.label}`;
      const s = series.get(key) ?? { label: h.label, points: [] };
      s.points.push({ at: r.period_end, v, raw: h }); series.set(key, s);
    }
  }
  return [...series.values()].filter((s) => s.points.length >= 2).sort((a, b) => b.points.length - a.points.length).slice(0, 4);
}
function Spark({ points }: { points: number[] }) {
  const min = Math.min(...points), max = Math.max(...points), w = 240, h = 40;
  const xy = points.map((p, i) => `${Math.round((i / (points.length - 1)) * w)},${Math.round(h - 4 - ((p - min) / (max - min || 1)) * (h - 8))}`).join(' ');
  return <svg className="rt-spark" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" aria-hidden="true"><polyline points={xy} /></svg>;
}
function Delta({ d }: { d?: Headline['delta'] }) {
  if (!d?.text) return null;
  return <span className={`rt-delta rt-delta--${d.dir === 'down' ? 'down' : d.dir === 'up' ? 'up' : 'flat'}`}>{d.dir === 'up' ? '↑' : d.dir === 'down' ? '↓' : ''}{d.text.replace(/^[+]/, '+')}</span>;
}

export function ReportsTimeline({ reports, work, onAsk }: { reports: TimelineReport[]; work: WorkItem[]; onAsk: (r: TimelineReport) => void }) {
  const [filter, setFilter] = useState<string>('All');
  const [reading, setReading] = useState<string | null>(null);
  const kinds = useMemo(() => ['All', ...new Set(reports.map((r) => KIND(r.type))), ...(reports.some((r) => WEEKLY(r.type)) ? ['Weekly'] : [])], [reports]);
  const shown = reports.filter((r) => filter === 'All' || (filter === 'Weekly' ? WEEKLY(r.type) : KIND(r.type) === filter));
  const tiles = useMemo(() => trends(shown), [shown]);
  if (!reports.length) return <section className="pt-col"><h1 className="pt-title">Your reports</h1><Empty icon="harbor" title="No reports yet">Your reports will be here as soon as they’re published.</Empty></section>;
  if (reading) return <Reader reports={shown.length ? shown : reports} id={reading} onClose={() => setReading(null)} onPick={setReading} onAsk={onAsk} />;
  // Work done between one report and the one before it (newest first), a line or two each.
  const between = (i: number) => {
    const r = shown[i]!, prev = shown[i + 1];
    const items = work.filter((w) => w.on_day <= r.period_end && (!prev || w.on_day > prev.period_end));
    return items.length ? `${items.slice(0, 2).map((w) => w.title).join('; ')}${items.length > 2 ? ` and ${items.length - 2} more` : ''}` : '';
  };
  let lastMonth = '';
  return (
    <section className="pt-col rt" data-testid="portal-reports">
      <div className="rt-head"><div><div className="rt-eyebrow">Your reports</div><h1 className="pt-title">What we did, and what moved</h1></div>
        <div className="rt-filters" role="tablist" aria-label="Kind of report">{kinds.map((k) => <button key={k} type="button" role="tab" aria-selected={filter === k} onClick={() => setFilter(k)}>{k}</button>)}</div></div>
      {tiles.length > 0 && <div className="rt-tiles">{tiles.map((t) => {
        const last = t.points[t.points.length - 1]!.raw;
        return <div key={t.label} className="rt-tile" data-testid="report-trend"><small>{t.label}</small><b>{last.value} <Delta d={last.delta} /></b><Spark points={t.points.map((p) => p.v)} /><small>across {t.points.length} reports</small></div>;
      })}</div>}
      <ol className="rt-line">{shown.map((r, i) => {
        const m = monthOf(r.period_end), head = m !== lastMonth; lastMonth = m;
        const did = between(i);
        return (
          <li key={r.id}>
            {head && <div className="rt-month">{m}</div>}
            <article className="rt-card" data-testid="portal-report">
              <div className="rt-card-top">
                <div><span className={`rt-chip rt-chip--${KIND(r.type).toLowerCase().replace(/\s+/g, '-')}`}>{KIND(r.type)}{WEEKLY(r.type) ? ' · weekly' : ''}</span> <b>{r.title.replace(/^.*?—\s*/, '')}</b>
                  <div className="rt-meta">{periodOf(r)}{r.published_at ? ` · out ${day(r.published_at)}` : ''}</div></div>
                <div className="rt-acts"><button type="button" className="cx-btn cx-btn--sm cx-btn--primary" onClick={() => setReading(r.id)} data-testid="report-read">Read</button>
                  <button type="button" className="cx-btn cx-btn--sm" onClick={() => onAsk(r)}>Ask about it</button></div>
              </div>
              {!!r.headline?.length && <div className="rt-stats">{r.headline.slice(0, 4).map((h) => <div key={h.label}><small>{h.label}</small><b>{h.value} <Delta d={h.delta} /></b></div>)}</div>}
            </article>
            {did && <div className="rt-did"><b>We did:</b> {did}</div>}
          </li>
        );
      })}</ol>
    </section>
  );
}

/** R3: the report itself, here — reports by month down the side, ‹ › through time, and "Ask about it". */
function Reader({ reports, id, onClose, onPick, onAsk }: { reports: TimelineReport[]; id: string; onClose: () => void; onPick: (id: string) => void; onAsk: (r: TimelineReport) => void }) {
  const i = Math.max(0, reports.findIndex((r) => r.id === id));
  const r = reports[i]!;
  const older = reports[i + 1], newer = reports[i - 1];
  const months = new Map<string, TimelineReport[]>();
  for (const x of reports) { const m = monthOf(x.period_end); months.set(m, [...(months.get(m) ?? []), x]); }
  return (
    <section className="rt-reader" data-testid="report-reader">
      <nav className="rt-nav" aria-label="Your reports">
        <button type="button" className="cx-link-btn" onClick={onClose}>‹ All reports</button>
        {[...months.entries()].map(([m, xs]) => <div key={m}><div className="rt-nav-month">{m}</div>
          {xs.map((x) => <button key={x.id} type="button" aria-current={x.id === r.id} onClick={() => onPick(x.id)}>{x.title.replace(/^.*?—\s*/, '')}<small>{periodOf(x)}</small></button>)}</div>)}
      </nav>
      <div className="rt-doc">
        <div className="rt-doc-bar">
          <button type="button" className="cx-btn cx-btn--sm" aria-label="Earlier report" disabled={!older} onClick={() => older && onPick(older.id)}>‹</button>
          <div className="rt-doc-title"><b>{r.title.replace(/^.*?—\s*/, '')}</b><small>{periodOf(r)}{r.published_at ? ` · out ${day(r.published_at)}` : ''}</small></div>
          <a className="cx-btn cx-btn--sm" href={`/api/v1/delivery/reports/${r.id}/view`} target="_blank" rel="noopener">Open in a new tab</a>
          <button type="button" className="cx-btn cx-btn--sm cx-btn--primary" onClick={() => onAsk(r)}>Ask about this report</button>
          <button type="button" className="cx-btn cx-btn--sm" aria-label="Later report" disabled={!newer} onClick={() => newer && onPick(newer.id)}>›</button>
        </div>
        <iframe key={r.id} className="rt-frame" src={`/api/v1/delivery/reports/${r.id}/view`} title={r.title} />
      </div>
    </section>
  );
}
